import React, { useEffect, useMemo, useState } from 'react';
import {
  Activity, AlertTriangle, BadgeCheck, Check, ClipboardList,
  Edit3, FileText, Mail, Package, Phone, Plus, Search,
  Star, Tag, Trash2, Truck, UserCheck, Users, X, Loader,
  CheckCircle, AlertCircle, Globe, Building2
} from 'lucide-react';
import {
  addFournisseurArticle, addFournisseurCertification, addFournisseurContact,
  bulkFournisseursActions, createFournisseur, deleteFournisseur,
  deleteFournisseurArticle, deleteFournisseurCertification, deleteFournisseurContact,
  evaluerFournisseur, getFournisseurArticles, getFournisseurCommandes,
  getFournisseurRuptures, getFournisseurStatistiques, getFournisseurs, getProduits,
  searchFournisseurs, setFournisseurContactPrincipal, setFournisseurPrincipal,
  toggleActiveFournisseur, updateFournisseur, updateFournisseurCategorie,
  updateFournisseurConditionsPaiement, updateFournisseurContact,
  updateFournisseurCriticite, updateFournisseurNotes, updateFournisseurScore,
  updateFournisseurTags
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../styles/Fournisseurs.css';

const emptySupplier = {
  nom:'', contact:'', telephone:'', email:'', adresse:'', siteWeb:'',
  categorie:'autre', criticite:'standard', scorePerformance:'', tags:'',
  delaiPaiement:'', remiseHabituelle:'', devise:'XOF', notesInternes:''
};
const emptyContact = { nom:'', poste:'', telephone:'', telephoneMobile:'', email:'', principal:false, notes:'' };
const emptyArticle = { produit:'', referenceFournisseur:'', designation:'', prixAchatActuel:'', quantiteMinimumCommande:1, conditionnement:'', quantiteParConditionnement:1, delaiLivraisonJours:0, disponible:true, fournisseurPrincipal:false, noteQualite:'', notes:'' };

const CATEGORIES = [['matiere-premiere','Matière première'],['equipement','Équipement'],['service','Service'],['technologie','Technologie'],['distribution','Distribution'],['autre','Autre']];
const CRITICITES = [['strategique','Stratégique'],['important','Important'],['standard','Standard']];
const TABS = [['resume','Résumé',Activity],['contacts','Contacts',Users],['articles','Articles',Package],['commandes','Commandes',ClipboardList],['commerce','Commerce',FileText],['alertes','Alertes',AlertTriangle]];

const CRIT_COLORS = { strategique:'#ef4444', important:'#f59e0b', standard:'#10b981' };
const CRIT_BG    = { strategique:'#fef2f2', important:'#fffbeb', standard:'#ecfdf5' };

const toTags = (v) => v.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
const money  = (v) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits:0 }).format(v||0);
const fdate  = (v) => v ? new Date(v).toLocaleDateString('fr-FR') : '—';

const Fournisseurs = () => {
  const { user } = useAuth();
  const canManage = ['admin-entreprise','super-admin'].includes(user?.role);

  const [fournisseurs,    setFournisseurs]    = useState([]);
  const [produits,        setProduits]        = useState([]);
  const [selected,        setSelected]        = useState(null);
  const [selectedIds,     setSelectedIds]     = useState([]);
  const [activeTab,       setActiveTab]       = useState('resume');
  const [loading,         setLoading]         = useState(true);
  const [saving,          setSaving]          = useState(false);
  const [toast,           setToast]           = useState(null);
  const [showModal,       setShowModal]       = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [formData,        setFormData]        = useState(emptySupplier);
  const [filters, setFilters] = useState({ nom:'', actif:'', categorie:'', criticite:'', scoreMin:'', tags:'' });
  const [bulk, setBulk] = useState({ action:'', categorie:'autre', criticite:'standard', tags:'' });
  const [detail, setDetail] = useState({ stats:null, articles:[], commandes:[], ruptures:[] });
  const [detailLoading, setDetailLoading] = useState(false);
  const [contactForm,      setContactForm]      = useState(emptyContact);
  const [editingContactId, setEditingContactId] = useState(null);
  const [articleForm,      setArticleForm]      = useState(emptyArticle);
  const [certificationForm,setCertificationForm]= useState({ nom:'', dateExpiration:'', documentUrl:'' });
  const [evaluationForm,   setEvaluationForm]   = useState({ note:'', commentaire:'' });

  useEffect(() => { loadInitialData(); }, []); // eslint-disable-line
  useEffect(() => { if (selected?._id) loadDetails(selected._id); }, [selected?._id]); // eslint-disable-line

  const supplierById = useMemo(() => new Map(fournisseurs.map(f => [f._id, f])), [fournisseurs]);

  const showToast = (type, text) => {
    setToast({ type, text });
    window.setTimeout(() => setToast(null), 4000);
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [fr, pr] = await Promise.all([getFournisseurs(), getProduits()]);
      const data = fr.data.data || [];
      setFournisseurs(data); setProduits(pr.data.data || []);
      setSelected(s => s || data[0] || null);
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const reloadFournisseurs = async (keepId = selected?._id) => {
    const res = await getFournisseurs();
    const data = res.data.data || [];
    setFournisseurs(data);
    setSelected(keepId ? data.find(f => f._id === keepId) || data[0] || null : data[0] || null);
  };

  const loadDetails = async (id) => {
    try {
      setDetailLoading(true);
      const [sr, ar, cr, rr] = await Promise.all([
        getFournisseurStatistiques(id), getFournisseurArticles(id),
        getFournisseurCommandes(id, { limit:20 }), getFournisseurRuptures(id)
      ]);
      setDetail({ stats:sr.data.data, articles:ar.data.data||[], commandes:cr.data.data||[], ruptures:rr.data.data||[] });
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur chargement fiche'); }
    finally { setDetailLoading(false); }
  };

  const applySearch = async () => {
    try {
      setLoading(true);
      const payload = {
        ...(filters.nom && { nom:filters.nom }),
        ...(filters.actif !== '' && { actif: filters.actif === 'true' }),
        ...(filters.categorie && { categories:[filters.categorie] }),
        ...(filters.criticite && { criticite:[filters.criticite] }),
        ...(filters.scoreMin && { scoreMin: Number(filters.scoreMin) }),
        ...(filters.tags && { tags: toTags(filters.tags) })
      };
      const res = Object.keys(payload).length ? await searchFournisseurs(payload) : await getFournisseurs();
      const data = res.data.data || [];
      setFournisseurs(data);
      setSelected(data.find(f => f._id === selected?._id) || data[0] || null);
      setSelectedIds([]);
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur recherche'); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setEditingSupplier(null); setFormData(emptySupplier); setShowModal(true); };
  const openEdit = (f) => {
    setEditingSupplier(f);
    setFormData({ nom:f.nom||'', contact:f.contact||'', telephone:f.telephone||'', email:f.email||'', adresse:f.adresse||'', siteWeb:f.siteWeb||'', categorie:f.categorie||'autre', criticite:f.criticite||'standard', scorePerformance:f.scorePerformance??'', tags:f.tags?.join(', ')||'', delaiPaiement:f.conditionsPaiement?.delaiPaiement??'', remiseHabituelle:f.conditionsPaiement?.remiseHabituelle??'', devise:f.conditionsPaiement?.devise||'XOF', notesInternes:f.notesInternes||'' });
    setShowModal(true);
  };

  const supplierPayload = () => ({ nom:formData.nom.trim(), contact:formData.contact.trim(), telephone:formData.telephone.trim(), email:formData.email.trim(), adresse:formData.adresse.trim(), siteWeb:formData.siteWeb.trim(), categorie:formData.categorie, criticite:formData.criticite, tags:toTags(formData.tags), scorePerformance:formData.scorePerformance===''?null:Number(formData.scorePerformance), conditionsPaiement:{ delaiPaiement:formData.delaiPaiement===''?undefined:Number(formData.delaiPaiement), remiseHabituelle:formData.remiseHabituelle===''?undefined:Number(formData.remiseHabituelle), devise:formData.devise||'XOF' }, notesInternes:formData.notesInternes });

  const handleSubmitSupplier = async (e) => {
    e.preventDefault();
    if (!formData.nom.trim()) { showToast('error', 'Le nom est requis'); return; }
    try {
      setSaving(true);
      if (editingSupplier) { await updateFournisseur(editingSupplier._id, supplierPayload()); showToast('success', 'Fournisseur modifié'); }
      else { await createFournisseur(supplierPayload()); showToast('success', 'Fournisseur créé'); }
      setShowModal(false);
      await reloadFournisseurs(editingSupplier?._id);
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (f) => {
    if (!window.confirm(`Supprimer "${f.nom}" ?`)) return;
    try { await deleteFournisseur(f._id); showToast('success', 'Fournisseur supprimé'); await reloadFournisseurs(null); }
    catch (err) { showToast('error', err.response?.data?.message || 'Erreur suppression'); }
  };

  const handleToggleActive = async (f) => {
    try { await toggleActiveFournisseur(f._id); showToast('success', f.actif ? 'Désactivé' : 'Activé'); await reloadFournisseurs(f._id); }
    catch (err) { showToast('error', err.response?.data?.message || 'Erreur statut'); }
  };

  const toggleSelection = (id) => setSelectedIds(c => c.includes(id) ? c.filter(i => i !== id) : [...c, id]);

  const runBulkAction = async () => {
    if (!bulk.action || !selectedIds.length) return;
    const data = {};
    if (bulk.action === 'update-categorie') data.categorie = bulk.categorie;
    if (bulk.action === 'update-criticite') data.criticite = bulk.criticite;
    if (bulk.action === 'add-tags') data.tags = toTags(bulk.tags);
    try {
      await bulkFournisseursActions({ fournisseurIds:selectedIds, action:bulk.action, data });
      showToast('success', 'Action en masse exécutée'); setSelectedIds([]);
      await reloadFournisseurs(selected?._id);
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur action masse'); }
  };

  const saveQuickProfile = async () => {
    if (!selected) return;
    try {
      await Promise.all([
        updateFournisseurCategorie(selected._id, { categorie: selected.categorie||'autre' }),
        updateFournisseurCriticite(selected._id, { criticite: selected.criticite||'standard' }),
        updateFournisseurTags(selected._id, { tags: selected.tags||[] }),
        updateFournisseurScore(selected._id, { scorePerformance: selected.scorePerformance??null })
      ]);
      showToast('success', 'Classification mise à jour'); await reloadFournisseurs(selected._id);
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur mise à jour'); }
  };

  const saveConditions = async () => {
    if (!selected) return;
    try {
      await updateFournisseurConditionsPaiement(selected._id, { delaiPaiement:selected.conditionsPaiement?.delaiPaiement||0, remiseHabituelle:selected.conditionsPaiement?.remiseHabituelle||0, devise:selected.conditionsPaiement?.devise||'XOF' });
      await updateFournisseurNotes(selected._id, { notesInternes: selected.notesInternes||'' });
      showToast('success', 'Conditions mises à jour'); await reloadFournisseurs(selected._id);
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur conditions'); }
  };

  const saveEvaluation = async (e) => {
    e.preventDefault();
    if (!selected || evaluationForm.note === '') return;
    try {
      await evaluerFournisseur(selected._id, { note:Number(evaluationForm.note), commentaire:evaluationForm.commentaire });
      setEvaluationForm({ note:'', commentaire:'' }); showToast('success', 'Évaluation enregistrée');
      await reloadFournisseurs(selected._id);
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur évaluation'); }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    if (!selected || !contactForm.nom.trim()) return;
    try {
      if (editingContactId) await updateFournisseurContact(selected._id, editingContactId, contactForm);
      else await addFournisseurContact(selected._id, contactForm);
      showToast('success', 'Contact enregistré'); setContactForm(emptyContact); setEditingContactId(null);
      await reloadFournisseurs(selected._id);
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur contact'); }
  };

  const editContact = (c) => { setEditingContactId(c._id); setContactForm({ nom:c.nom||'', poste:c.poste||'', telephone:c.telephone||'', telephoneMobile:c.telephoneMobile||'', email:c.email||'', principal:Boolean(c.principal), notes:c.notes||'' }); };
  const removeContact = async (cid) => { if (!selected || !window.confirm('Supprimer ce contact ?')) return; try { await deleteFournisseurContact(selected._id, cid); showToast('success', 'Contact supprimé'); await reloadFournisseurs(selected._id); } catch (err) { showToast('error', err.response?.data?.message||'Erreur'); } };
  const setPrincipalContact = async (cid) => { try { await setFournisseurContactPrincipal(selected._id, cid); showToast('success', 'Contact principal mis à jour'); await reloadFournisseurs(selected._id); } catch (err) { showToast('error', err.response?.data?.message||'Erreur'); } };

  const submitArticle = async (e) => {
    e.preventDefault();
    if (!selected || !articleForm.produit || articleForm.prixAchatActuel === '') return;
    try {
      await addFournisseurArticle(selected._id, { ...articleForm, prixAchatActuel:Number(articleForm.prixAchatActuel), quantiteMinimumCommande:Number(articleForm.quantiteMinimumCommande||1), quantiteParConditionnement:Number(articleForm.quantiteParConditionnement||1), delaiLivraisonJours:Number(articleForm.delaiLivraisonJours||0), noteQualite:articleForm.noteQualite===''?undefined:Number(articleForm.noteQualite) });
      setArticleForm(emptyArticle); showToast('success', 'Article ajouté'); await loadDetails(selected._id);
    } catch (err) { showToast('error', err.response?.data?.message||'Erreur article'); }
  };

  const removeArticle = async (aid) => { if (!selected || !window.confirm('Supprimer cet article ?')) return; try { await deleteFournisseurArticle(selected._id, aid); showToast('success', 'Article supprimé'); await loadDetails(selected._id); } catch (err) { showToast('error', err.response?.data?.message||'Erreur'); } };

  const submitCertification = async (e) => {
    e.preventDefault();
    if (!selected || !certificationForm.nom.trim()) return;
    try { await addFournisseurCertification(selected._id, certificationForm); setCertificationForm({ nom:'', dateExpiration:'', documentUrl:'' }); showToast('success', 'Certification ajoutée'); await reloadFournisseurs(selected._id); }
    catch (err) { showToast('error', err.response?.data?.message||'Erreur certification'); }
  };

  const removeCertification = async (cid) => { if (!selected || !window.confirm('Supprimer cette certification ?')) return; try { await deleteFournisseurCertification(selected._id, cid); showToast('success', 'Certification supprimée'); await reloadFournisseurs(selected._id); } catch (err) { showToast('error', err.response?.data?.message||'Erreur'); } };

  const markSupplierPrincipal = async () => {
    if (!selected || !window.confirm('Marquer comme fournisseur principal ?')) return;
    try { await setFournisseurPrincipal(selected._id); showToast('success', 'Fournisseur principal mis à jour'); await loadDetails(selected._id); }
    catch (err) { showToast('error', err.response?.data?.message||'Erreur'); }
  };

  const updateSelected = (patch) => setSelected(c => ({ ...c, ...patch }));
  const updateSelectedConditions = (patch) => setSelected(c => ({ ...c, conditionsPaiement: { ...(c.conditionsPaiement||{}), ...patch } }));

  const nbActifs   = fournisseurs.filter(f => f.actif).length;
  const scoreMoyen = fournisseurs.filter(f => f.scorePerformance != null).length
    ? Math.round(fournisseurs.filter(f => f.scorePerformance != null).reduce((s,f) => s + f.scorePerformance, 0) / fournisseurs.filter(f => f.scorePerformance != null).length)
    : null;

  if (loading) return (
    <div className="fo-loading"><Loader className="fo-spinner" size={26}/><span>Chargement des fournisseurs…</span></div>
  );

  return (
    <div className="fo-page">

      {/* Toast */}
      {toast && (
        <div className={`fo-toast fo-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={13}/> : <AlertCircle size={13}/>}
          {toast.text}
        </div>
      )}

      {/* ── Header ── */}
      <div className="fo-header">
        <div className="fo-header-left">
          <div className="fo-header-icon"><Truck size={20}/></div>
          <div>
            <h1 className="fo-header-title">Fournisseurs</h1>
            <p className="fo-header-sub">Contacts, achats, performance et alertes</p>
          </div>
        </div>
        {canManage && (
          <button className="fo-cta-btn" onClick={openCreate}>
            <Plus size={14}/> Nouveau fournisseur
          </button>
        )}
      </div>

      <div className="fo-content">

        {/* KPI row */}
        <div className="fo-kpi-row">
          {[
            { label:'Total',    value: fournisseurs.length, color:'#6366f1', bg:'#eef2ff', icon:Truck },
            { label:'Actifs',   value: nbActifs,             color:'#10b981', bg:'#ecfdf5', icon:CheckCircle },
            { label:'Inactifs', value: fournisseurs.length - nbActifs, color:'#94a3b8', bg:'#f1f5f9', icon:X },
            { label:'Score moy.',value: scoreMoyen != null ? `${scoreMoyen}/100` : '—', color:'#f59e0b', bg:'#fffbeb', icon:Star },
          ].map((k,i) => {
            const Icon = k.icon;
            return (
              <div key={i} className="fo-kpi" style={{ borderTopColor: k.color }}>
                <div className="fo-kpi-icon" style={{ background: k.bg, color: k.color }}><Icon size={15}/></div>
                <div className="fo-kpi-val">{k.value}</div>
                <div className="fo-kpi-label">{k.label}</div>
              </div>
            );
          })}
        </div>

        {/* Filter bar */}
        <div className="fo-filters">
          <div className="fo-search-wrap">
            <Search size={14} className="fo-search-ico"/>
            <input className="fo-search-input" value={filters.nom} placeholder="Rechercher un fournisseur…"
              onChange={e => setFilters({...filters, nom: e.target.value})}
              onKeyDown={e => e.key === 'Enter' && applySearch()}/>
          </div>
          <select className="fo-select" value={filters.actif} onChange={e => setFilters({...filters, actif: e.target.value})}>
            <option value="">Tous statuts</option>
            <option value="true">Actifs</option>
            <option value="false">Inactifs</option>
          </select>
          <select className="fo-select" value={filters.categorie} onChange={e => setFilters({...filters, categorie: e.target.value})}>
            <option value="">Toutes catégories</option>
            {CATEGORIES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select className="fo-select" value={filters.criticite} onChange={e => setFilters({...filters, criticite: e.target.value})}>
            <option value="">Toutes criticités</option>
            {CRITICITES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input className="fo-input-sm" type="number" min="0" max="100" value={filters.scoreMin} placeholder="Score min"
            onChange={e => setFilters({...filters, scoreMin: e.target.value})}/>
          <button className="fo-filter-btn" onClick={applySearch}>Filtrer</button>
        </div>

        {/* Bulk bar */}
        {canManage && selectedIds.length > 0 && (
          <div className="fo-bulk-bar">
            <span className="fo-bulk-count">{selectedIds.length} sélectionné(s)</span>
            <select className="fo-select" value={bulk.action} onChange={e => setBulk({...bulk, action: e.target.value})}>
              <option value="">Action en masse…</option>
              <option value="activate">Activer</option>
              <option value="deactivate">Désactiver</option>
              <option value="update-categorie">Changer catégorie</option>
              <option value="update-criticite">Changer criticité</option>
              <option value="add-tags">Ajouter tags</option>
            </select>
            {bulk.action === 'update-categorie' && (
              <select className="fo-select" value={bulk.categorie} onChange={e => setBulk({...bulk, categorie: e.target.value})}>
                {CATEGORIES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            )}
            {bulk.action === 'update-criticite' && (
              <select className="fo-select" value={bulk.criticite} onChange={e => setBulk({...bulk, criticite: e.target.value})}>
                {CRITICITES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            )}
            {bulk.action === 'add-tags' && (
              <input className="fo-input-sm" value={bulk.tags} placeholder="Tags à ajouter" onChange={e => setBulk({...bulk, tags: e.target.value})}/>
            )}
            <button className="fo-cta-btn" onClick={runBulkAction}>Appliquer</button>
          </div>
        )}

        {/* ── Split layout ── */}
        <div className="fo-split">

          {/* Left: list */}
          <div className="fo-list">
            {fournisseurs.length === 0 ? (
              <div className="fo-list-empty">
                <Truck size={32}/><p>Aucun fournisseur</p>
              </div>
            ) : fournisseurs.map(f => {
              const critColor = CRIT_COLORS[f.criticite] || '#94a3b8';
              const critBg    = CRIT_BG[f.criticite]    || '#f1f5f9';
              return (
                <button key={f._id}
                  className={`fo-list-row${selected?._id === f._id ? ' fo-list-row--active' : ''}`}
                  onClick={() => setSelected(supplierById.get(f._id))}>
                  {canManage && (
                    <input type="checkbox" className="fo-check"
                      checked={selectedIds.includes(f._id)}
                      onChange={() => toggleSelection(f._id)}
                      onClick={e => e.stopPropagation()}/>
                  )}
                  <div className="fo-list-av" style={{ background: critBg, color: critColor }}>
                    {f.nom?.charAt(0)?.toUpperCase() || 'F'}
                  </div>
                  <div className="fo-list-info">
                    <span className="fo-list-name">{f.nom}</span>
                    <span className="fo-list-sub">{f.email || f.telephone || 'Aucun contact'}</span>
                  </div>
                  <span className={`fo-status-dot ${f.actif ? 'fo-status-dot--on' : 'fo-status-dot--off'}`}/>
                </button>
              );
            })}
          </div>

          {/* Right: detail */}
          <div className="fo-detail">
            {!selected ? (
              <div className="fo-detail-empty">
                <Truck size={40}/>
                <p>Sélectionnez un fournisseur pour voir sa fiche</p>
              </div>
            ) : (
              <>
                {/* Detail header */}
                <div className="fo-detail-head">
                  <div className="fo-detail-av" style={{ background: CRIT_BG[selected.criticite]||'#f1f5f9', color: CRIT_COLORS[selected.criticite]||'#94a3b8' }}>
                    {selected.nom?.charAt(0)?.toUpperCase() || 'F'}
                  </div>
                  <div className="fo-detail-info">
                    <div className="fo-detail-name">
                      {selected.nom}
                      <span className="fo-crit-badge" style={{ background: CRIT_BG[selected.criticite]||'#f1f5f9', color: CRIT_COLORS[selected.criticite]||'#94a3b8' }}>
                        {selected.criticite || 'standard'}
                      </span>
                      <span className={`fo-active-badge ${selected.actif ? 'fo-active-badge--on' : 'fo-active-badge--off'}`}>
                        {selected.actif ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                    <div className="fo-detail-meta">
                      {selected.telephone && <span><Phone size={11}/> {selected.telephone}</span>}
                      {selected.email     && <span><Mail  size={11}/> {selected.email}</span>}
                      {selected.siteWeb   && <span><Globe size={11}/> {selected.siteWeb}</span>}
                      <span><Tag size={11}/> {selected.categorie || 'autre'}</span>
                    </div>
                    {selected.tags?.length > 0 && (
                      <div className="fo-tags-row">
                        {selected.tags.map(t => <span key={t} className="fo-tag">{t}</span>)}
                      </div>
                    )}
                  </div>
                  {canManage && (
                    <div className="fo-detail-actions">
                      <button className="fo-ico-btn fo-ico-btn--edit"   onClick={() => openEdit(selected)} title="Modifier"><Edit3 size={14}/></button>
                      <button className="fo-ico-btn fo-ico-btn--toggle" onClick={() => handleToggleActive(selected)} title={selected.actif ? 'Désactiver' : 'Activer'}><Check size={14}/></button>
                      <button className="fo-ico-btn fo-ico-btn--del"    onClick={() => handleDelete(selected)} title="Supprimer"><Trash2 size={14}/></button>
                    </div>
                  )}
                </div>

                {/* Tabs */}
                <div className="fo-tabs">
                  {TABS.map(([id, label, Icon]) => (
                    <button key={id} className={`fo-tab${activeTab===id?' fo-tab--active':''}`} onClick={() => setActiveTab(id)}>
                      <Icon size={13}/> {label}
                    </button>
                  ))}
                </div>

                {/* Tab content */}
                {detailLoading ? (
                  <div className="fo-tab-loading"><Loader size={20} className="fo-spin"/><span>Chargement…</span></div>
                ) : (
                  <div className="fo-tab-body">

                    {/* ─ Résumé ─ */}
                    {activeTab === 'resume' && (
                      <div className="fo-resume">
                        <div className="fo-metrics">
                          {[
                            { label:'Dette actuelle',  value:`${money(selected.detteActuelle)} XOF` },
                            { label:'Commandes',       value: detail.stats?.commandes?.total || 0 },
                            { label:'Articles dispo',  value: detail.stats?.articlesDisponibles || 0 },
                            { label:'Score',           value: selected.scorePerformance != null ? `${selected.scorePerformance}/100` : '—' },
                          ].map((m,i) => (
                            <div key={i} className="fo-metric">
                              <span>{m.label}</span>
                              <strong>{m.value}</strong>
                            </div>
                          ))}
                        </div>

                        <div className="fo-section">
                          <div className="fo-section-title">Classification</div>
                          <div className="fo-form-row">
                            <div className="fo-field">
                              <label>Catégorie</label>
                              <select value={selected.categorie||'autre'} onChange={e => updateSelected({ categorie: e.target.value })} disabled={!canManage}>
                                {CATEGORIES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                              </select>
                            </div>
                            <div className="fo-field">
                              <label>Criticité</label>
                              <select value={selected.criticite||'standard'} onChange={e => updateSelected({ criticite: e.target.value })} disabled={!canManage}>
                                {CRITICITES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                              </select>
                            </div>
                            <div className="fo-field">
                              <label>Score (0-100)</label>
                              <input type="number" min="0" max="100" value={selected.scorePerformance??''} onChange={e => updateSelected({ scorePerformance: e.target.value===''?null:Number(e.target.value) })} disabled={!canManage} placeholder="Score"/>
                            </div>
                            <div className="fo-field">
                              <label>Tags</label>
                              <input value={selected.tags?.join(', ')||''} onChange={e => updateSelected({ tags: toTags(e.target.value) })} disabled={!canManage} placeholder="tag1, tag2"/>
                            </div>
                          </div>
                          {canManage && <button className="fo-save-btn" onClick={saveQuickProfile}><BadgeCheck size={13}/> Enregistrer</button>}
                        </div>

                        <div className="fo-section">
                          <div className="fo-section-title">Évaluation rapide</div>
                          <form onSubmit={saveEvaluation}>
                            <div className="fo-form-row">
                              <div className="fo-field">
                                <label>Note / 100</label>
                                <input type="number" min="0" max="100" value={evaluationForm.note} onChange={e => setEvaluationForm({...evaluationForm, note: e.target.value})} disabled={!canManage} placeholder="Note"/>
                              </div>
                              <div className="fo-field fo-field--grow">
                                <label>Commentaire</label>
                                <input value={evaluationForm.commentaire} onChange={e => setEvaluationForm({...evaluationForm, commentaire: e.target.value})} disabled={!canManage} placeholder="Commentaire interne"/>
                              </div>
                            </div>
                            {canManage && <button className="fo-save-btn" type="submit"><Star size={13}/> Évaluer</button>}
                          </form>
                        </div>
                      </div>
                    )}

                    {/* ─ Contacts ─ */}
                    {activeTab === 'contacts' && (
                      <div className="fo-split-panel">
                        <div className="fo-section">
                          <div className="fo-section-title">Contacts</div>
                          {!(selected.contacts||[]).length
                            ? <p className="fo-muted">Aucun contact renseigné.</p>
                            : (selected.contacts||[]).map(c => (
                              <div className="fo-item-row" key={c._id}>
                                <div className="fo-item-av"><Users size={14}/></div>
                                <div className="fo-item-info">
                                  <strong>{c.nom}</strong>
                                  <span>{c.poste||'—'} · {c.email||c.telephone||'—'}</span>
                                </div>
                                {c.principal && <span className="fo-principal-badge">Principal</span>}
                                {canManage && (
                                  <div className="fo-item-actions">
                                    <button className="fo-mini-btn" onClick={() => editContact(c)}><Edit3 size={12}/></button>
                                    <button className="fo-mini-btn" onClick={() => setPrincipalContact(c._id)}><UserCheck size={12}/></button>
                                    <button className="fo-mini-btn fo-mini-btn--del" onClick={() => removeContact(c._id)}><Trash2 size={12}/></button>
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                        {canManage && (
                          <form className="fo-section" onSubmit={submitContact}>
                            <div className="fo-section-title">{editingContactId ? 'Modifier contact' : 'Ajouter contact'}</div>
                            <div className="fo-field"><label>Nom *</label><input value={contactForm.nom} onChange={e => setContactForm({...contactForm, nom:e.target.value})} required/></div>
                            <div className="fo-form-row">
                              <div className="fo-field"><label>Poste</label><input value={contactForm.poste} onChange={e => setContactForm({...contactForm, poste:e.target.value})}/></div>
                              <div className="fo-field"><label>Email</label><input type="email" value={contactForm.email} onChange={e => setContactForm({...contactForm, email:e.target.value})}/></div>
                            </div>
                            <div className="fo-form-row">
                              <div className="fo-field"><label>Téléphone</label><input value={contactForm.telephone} onChange={e => setContactForm({...contactForm, telephone:e.target.value})}/></div>
                              <div className="fo-field"><label>Mobile</label><input value={contactForm.telephoneMobile} onChange={e => setContactForm({...contactForm, telephoneMobile:e.target.value})}/></div>
                            </div>
                            <div className="fo-field"><label>Notes</label><textarea value={contactForm.notes} onChange={e => setContactForm({...contactForm, notes:e.target.value})}/></div>
                            <label className="fo-check-line"><input type="checkbox" checked={contactForm.principal} onChange={e => setContactForm({...contactForm, principal:e.target.checked})}/> Contact principal</label>
                            <button className="fo-save-btn" type="submit"><Check size={13}/> Enregistrer</button>
                          </form>
                        )}
                      </div>
                    )}

                    {/* ─ Articles ─ */}
                    {activeTab === 'articles' && (
                      <div className="fo-split-panel">
                        <div className="fo-section">
                          <div className="fo-section-title-row">
                            <span className="fo-section-title" style={{margin:0}}>Articles fournisseur</span>
                            {canManage && <button className="fo-outline-btn" onClick={markSupplierPrincipal}><Star size={12}/> Principal</button>}
                          </div>
                          {!detail.articles.length
                            ? <p className="fo-muted">Aucun article lié.</p>
                            : detail.articles.map(a => (
                              <div className="fo-item-row" key={a._id}>
                                <div className="fo-item-av" style={{background:'#eef2ff',color:'#6366f1'}}><Package size={13}/></div>
                                <div className="fo-item-info">
                                  <strong>{a.produit?.nom||a.designation||'Article'}</strong>
                                  <span>{a.referenceFournisseur||'—'} · {money(a.prixAchatActuel)} XOF · {a.delaiLivraisonJours||0}j</span>
                                </div>
                                <span className={`fo-avail-badge ${a.disponible?'fo-avail-badge--ok':'fo-avail-badge--off'}`}>{a.disponible?'Dispo':'Indispo'}</span>
                                {canManage && <button className="fo-mini-btn fo-mini-btn--del" onClick={() => removeArticle(a._id)}><Trash2 size={12}/></button>}
                              </div>
                            ))}
                        </div>
                        {canManage && (
                          <form className="fo-section" onSubmit={submitArticle}>
                            <div className="fo-section-title">Ajouter article</div>
                            <div className="fo-field"><label>Produit *</label><select value={articleForm.produit} onChange={e => setArticleForm({...articleForm,produit:e.target.value})} required><option value="">Sélectionner</option>{produits.map(p => <option key={p._id} value={p._id}>{p.nom}</option>)}</select></div>
                            <div className="fo-form-row">
                              <div className="fo-field"><label>Réf. fournisseur</label><input value={articleForm.referenceFournisseur} onChange={e => setArticleForm({...articleForm,referenceFournisseur:e.target.value})}/></div>
                              <div className="fo-field"><label>Prix achat *</label><input type="number" min="0" value={articleForm.prixAchatActuel} onChange={e => setArticleForm({...articleForm,prixAchatActuel:e.target.value})} required/></div>
                            </div>
                            <div className="fo-form-row">
                              <div className="fo-field"><label>Qté min</label><input type="number" min="1" value={articleForm.quantiteMinimumCommande} onChange={e => setArticleForm({...articleForm,quantiteMinimumCommande:e.target.value})}/></div>
                              <div className="fo-field"><label>Délai (j)</label><input type="number" min="0" value={articleForm.delaiLivraisonJours} onChange={e => setArticleForm({...articleForm,delaiLivraisonJours:e.target.value})}/></div>
                            </div>
                            <label className="fo-check-line"><input type="checkbox" checked={articleForm.disponible} onChange={e => setArticleForm({...articleForm,disponible:e.target.checked})}/> Disponible</label>
                            <label className="fo-check-line"><input type="checkbox" checked={articleForm.fournisseurPrincipal} onChange={e => setArticleForm({...articleForm,fournisseurPrincipal:e.target.checked})}/> Fournisseur principal</label>
                            <button className="fo-save-btn" type="submit"><Plus size={13}/> Ajouter</button>
                          </form>
                        )}
                      </div>
                    )}

                    {/* ─ Commandes ─ */}
                    {activeTab === 'commandes' && (
                      <div className="fo-section">
                        <div className="fo-section-title">Historique commandes</div>
                        {!detail.commandes.length ? <p className="fo-muted">Aucune commande.</p> : (
                          <table className="fo-table">
                            <thead><tr><th>Numéro</th><th>Date</th><th>Statut</th><th>Total</th><th>Reste</th></tr></thead>
                            <tbody>
                              {detail.commandes.map(c => (
                                <tr key={c._id}>
                                  <td className="fo-mono">{c.numeroCommande}</td>
                                  <td>{fdate(c.dateCommande)}</td>
                                  <td><span className="fo-status-chip">{c.statut}</span></td>
                                  <td>{money(c.montantTotal)} XOF</td>
                                  <td>{money(c.resteAPayer)} XOF</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}

                    {/* ─ Commerce ─ */}
                    {activeTab === 'commerce' && (
                      <div className="fo-split-panel">
                        <div className="fo-section">
                          <div className="fo-section-title">Conditions commerciales</div>
                          <div className="fo-form-row">
                            <div className="fo-field"><label>Délai paiement (j)</label><input type="number" min="0" value={selected.conditionsPaiement?.delaiPaiement??''} onChange={e => updateSelectedConditions({ delaiPaiement:Number(e.target.value||0) })} disabled={!canManage}/></div>
                            <div className="fo-field"><label>Remise habituelle (%)</label><input type="number" min="0" max="100" value={selected.conditionsPaiement?.remiseHabituelle??''} onChange={e => updateSelectedConditions({ remiseHabituelle:Number(e.target.value||0) })} disabled={!canManage}/></div>
                            <div className="fo-field"><label>Devise</label><select value={selected.conditionsPaiement?.devise||'XOF'} onChange={e => updateSelectedConditions({ devise:e.target.value })} disabled={!canManage}><option value="XOF">XOF</option><option value="EUR">EUR</option><option value="USD">USD</option><option value="GBP">GBP</option></select></div>
                          </div>
                          <div className="fo-field"><label>Notes internes</label><textarea value={selected.notesInternes||''} onChange={e => updateSelected({ notesInternes:e.target.value })} disabled={!canManage}/></div>
                          {canManage && <button className="fo-save-btn" onClick={saveConditions}><Check size={13}/> Enregistrer</button>}
                        </div>

                        <div className="fo-section">
                          <div className="fo-section-title">Certifications</div>
                          {!(selected.certifications||[]).length && <p className="fo-muted">Aucune certification.</p>}
                          {(selected.certifications||[]).map(c => (
                            <div className="fo-item-row" key={c._id}>
                              <div className="fo-item-av" style={{background:'#ecfdf5',color:'#16a34a'}}><BadgeCheck size={13}/></div>
                              <div className="fo-item-info"><strong>{c.nom}</strong><span>Exp: {fdate(c.dateExpiration)}</span></div>
                              {canManage && <button className="fo-mini-btn fo-mini-btn--del" onClick={() => removeCertification(c._id)}><Trash2 size={12}/></button>}
                            </div>
                          ))}
                          {canManage && (
                            <form className="fo-inline-form" onSubmit={submitCertification}>
                              <input value={certificationForm.nom} onChange={e => setCertificationForm({...certificationForm,nom:e.target.value})} placeholder="Nom certification" required/>
                              <input type="date" value={certificationForm.dateExpiration} onChange={e => setCertificationForm({...certificationForm,dateExpiration:e.target.value})}/>
                              <input value={certificationForm.documentUrl} onChange={e => setCertificationForm({...certificationForm,documentUrl:e.target.value})} placeholder="URL document"/>
                              <button className="fo-outline-btn" type="submit"><Plus size={12}/> Ajouter</button>
                            </form>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ─ Alertes ─ */}
                    {activeTab === 'alertes' && (
                      <div className="fo-section">
                        <div className="fo-section-title">Ruptures fournisseur</div>
                        {!detail.ruptures.length ? <p className="fo-muted">Aucune rupture déclarée.</p> : detail.ruptures.map(r => (
                          <div className="fo-item-row" key={r._id}>
                            <div className="fo-item-av" style={{background:'#fef2f2',color:'#dc2626'}}><AlertTriangle size={13}/></div>
                            <div className="fo-item-info">
                              <strong>{r.produit?.nom||r.designation||'Article'}</strong>
                              <span>Retour prévu : {fdate(r.dateRetourStock)}</span>
                            </div>
                            <span className="fo-rupture-badge">Rupture</span>
                          </div>
                        ))}
                      </div>
                    )}

                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Modal Fournisseur ── */}
      {showModal && (
        <>
          <div className="fo-overlay" onClick={() => setShowModal(false)}/>
          <div className="fo-modal">
            <div className="fo-modal-head">
              <div>
                <h2 className="fo-modal-title">{editingSupplier ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}</h2>
                <p className="fo-modal-sub">{editingSupplier ? editingSupplier.nom : 'Renseigner les informations du fournisseur'}</p>
              </div>
              <button className="fo-modal-close" onClick={() => setShowModal(false)}><X size={16}/></button>
            </div>
            <form onSubmit={handleSubmitSupplier} className="fo-modal-body">

              <div className="fo-section-label">Informations générales</div>
              <div className="fo-form-row">
                <div className="fo-field"><label>Nom *</label><input value={formData.nom} onChange={e => setFormData({...formData,nom:e.target.value})} required autoFocus/></div>
                <div className="fo-field"><label>Site web</label><input value={formData.siteWeb} onChange={e => setFormData({...formData,siteWeb:e.target.value})} placeholder="https://…"/></div>
              </div>
              <div className="fo-form-row">
                <div className="fo-field"><label>Contact</label><input value={formData.contact} onChange={e => setFormData({...formData,contact:e.target.value})}/></div>
                <div className="fo-field"><label>Téléphone</label><input value={formData.telephone} onChange={e => setFormData({...formData,telephone:e.target.value})}/></div>
                <div className="fo-field"><label>Email</label><input type="email" value={formData.email} onChange={e => setFormData({...formData,email:e.target.value})}/></div>
              </div>
              <div className="fo-field"><label>Adresse</label><textarea rows="2" value={formData.adresse} onChange={e => setFormData({...formData,adresse:e.target.value})}/></div>

              <div className="fo-section-label">Classification</div>
              <div className="fo-form-row">
                <div className="fo-field"><label>Catégorie</label><select value={formData.categorie} onChange={e => setFormData({...formData,categorie:e.target.value})}>{CATEGORIES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select></div>
                <div className="fo-field"><label>Criticité</label><select value={formData.criticite} onChange={e => setFormData({...formData,criticite:e.target.value})}>{CRITICITES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select></div>
                <div className="fo-field"><label>Score (0-100)</label><input type="number" min="0" max="100" value={formData.scorePerformance} onChange={e => setFormData({...formData,scorePerformance:e.target.value})}/></div>
              </div>
              <div className="fo-field"><label>Tags <span className="fo-opt">(séparés par virgule)</span></label><input value={formData.tags} onChange={e => setFormData({...formData,tags:e.target.value})} placeholder="import, local, prioritaire"/></div>

              <div className="fo-section-label">Conditions commerciales</div>
              <div className="fo-form-row">
                <div className="fo-field"><label>Délai paiement (j)</label><input type="number" min="0" value={formData.delaiPaiement} onChange={e => setFormData({...formData,delaiPaiement:e.target.value})}/></div>
                <div className="fo-field"><label>Remise habituelle (%)</label><input type="number" min="0" max="100" value={formData.remiseHabituelle} onChange={e => setFormData({...formData,remiseHabituelle:e.target.value})}/></div>
                <div className="fo-field"><label>Devise</label><select value={formData.devise} onChange={e => setFormData({...formData,devise:e.target.value})}><option value="XOF">XOF</option><option value="EUR">EUR</option><option value="USD">USD</option><option value="GBP">GBP</option></select></div>
              </div>
              <div className="fo-field"><label>Notes internes</label><textarea rows="2" value={formData.notesInternes} onChange={e => setFormData({...formData,notesInternes:e.target.value})}/></div>

              <div className="fo-modal-footer">
                <button type="button" className="fo-btn-cancel" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="fo-btn-confirm" disabled={saving}>
                  {saving ? <><Loader size={13} className="fo-spin"/> Enregistrement…</> : <><Check size={13}/> {editingSupplier ? 'Enregistrer' : 'Créer'}</>}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default Fournisseurs;
